import { motion } from "framer-motion";
import { Camera, Smartphone, ToggleRight, ShieldCheck, Wifi, Watch } from "lucide-react";

export const PrivacyHardware = () => {
  return (
    <section data-testid="privacy-hardware-section" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="font-inter text-xs font-bold tracking-[0.3em] uppercase text-lime">Zero Hardware Costs</span>
          <h2 className="font-inter text-4xl md:text-5xl font-black text-white mt-4">
            Your Hardware. <span className="text-gold">Our Intelligence.</span>
          </h2>
          <p className="text-base md:text-lg text-slate-400 mt-4 max-w-2xl mx-auto">
            No expensive installs. No privacy risks. Argus plugs into what you already have.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <motion.div className="glass-card rounded-xl p-8 border border-lime/10" data-testid="privacy-card-cameras"
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0 }}>
            <div className="flex items-center gap-3 mb-4">
              <Camera className="w-6 h-6 text-lime" />
              <span className="text-xs font-inter font-bold tracking-wider text-lime">EXISTING CAMERAS</span>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed mb-4">
              Argus plugs into your current security system. We convert visual cues into text alerts instantly.
            </p>
            <div className="flex items-center gap-2 bg-lime/5 border border-lime/20 rounded px-3 py-2">
              <ShieldCheck className="w-4 h-4 text-lime" />
              <span className="text-xs text-lime font-bold">We never store video footage</span>
            </div>
          </motion.div>

          <motion.div className="glass-card rounded-xl p-8 border border-blue-400/10" data-testid="privacy-card-byod"
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <div className="flex items-center gap-3 mb-4">
              <Smartphone className="w-6 h-6 text-blue-400" />
              <span className="text-xs font-inter font-bold tracking-wider text-blue-400">STAFF BYOD</span>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed mb-4">
              Your team uses the gear they already love — Apple Watch, Android phone, or wireless earbuds. The Argus App stays in sync.
            </p>
            <div className="flex gap-3">
              <Watch className="w-5 h-5 text-slate-400" />
              <Smartphone className="w-5 h-5 text-slate-400" />
              <Wifi className="w-5 h-5 text-slate-400" />
            </div>
          </motion.div>

          <motion.div className="glass-card rounded-xl p-8 border border-gold/10" data-testid="privacy-card-toggle"
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
            <div className="flex items-center gap-3 mb-4">
              <ToggleRight className="w-6 h-6 text-gold" />
              <span className="text-xs font-inter font-bold tracking-wider text-gold">COMMUNICATION, THEIR WAY</span>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed mb-4">
              Staff toggle between <strong className="text-white">Whisper Mode</strong> (Voice AI in ear) or <strong className="text-white">Silent Alert</strong> (haptic text on wrist).
            </p>
            {/* Toggle mockup */}
            <div className="bg-indigo-700/60 rounded-lg p-3 border border-indigo-500/30">
              <div className="flex items-center justify-between text-xs">
                <span className="text-blue-400 font-bold">Voice</span>
                <div className="w-10 h-5 bg-gold/30 rounded-full relative">
                  <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-gold rounded-full" />
                </div>
                <span className="text-gold font-bold">Text</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Privacy edge processing */}
        <motion.div className="glass-card rounded-xl p-6 border border-lime/10 flex items-center gap-6 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
          <div className="w-12 h-12 rounded-full bg-lime/10 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-6 h-6 text-lime" />
          </div>
          <div>
            <p className="text-sm font-inter font-bold text-white mb-1">Edge Processing — Total Privacy Compliance</p>
            <p className="text-xs text-slate-400">Video frames are converted to text metadata and deleted instantly. No footage is ever stored or transmitted. No proprietary hardware required.</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
